import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad

Última actualización: 20 de abril de 2026

Tu privacidad es importante para nosotros. Esta Política de Privacidad describe cómo recopilamos, usamos y protegemos tu información personal cuando utilizas nuestro sitio web y adquieres el Protocolo Anti-Inflamación 72h.

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra, incluyendo tu nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito en nuestros servidores.

2. Uso de la Información
Utilizamos tu información personal para:
• Procesar y entregar tu compra.
• Enviarte el acceso al protocolo y materiales relacionados.
• Comunicarnos contigo sobre tu pedido.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración.

4. Compartir Información
No vendemos, intercambiamos ni transferimos tu información personal a terceros, excepto a proveedores de servicios de pago necesarios para procesar tu transacción.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies.

6. Tus Derechos
Tienes derecho a acceder, corregir o eliminar tu información personal. Para ejercer estos derechos, puedes contactarnos a través de los canales disponibles.

7. Cambios a esta Política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: 20 de abril de 2026

Al adquirir el Protocolo Anti-Inflamación 72h, aceptas los siguientes términos y condiciones.

1. Producto Digital
El Protocolo Anti-Inflamación 72h es un producto digital de carácter informativo y educativo. No sustituye el consejo médico profesional, diagnóstico o tratamiento.

2. Acceso al Producto
Una vez completada la compra, recibirás acceso inmediato al protocolo y todos los materiales incluidos. El acceso es de por vida con un pago único.

3. Uso Personal
El contenido del protocolo es para uso personal exclusivamente. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.

4. Descargo de Responsabilidad
Los resultados pueden variar de persona a persona. Este protocolo no garantiza resultados específicos. Si tienes condiciones médicas preexistentes, consulta a tu médico antes de seguir cualquier plan alimenticio.

5. Propiedad Intelectual
Todo el contenido, incluyendo textos, gráficos, guías y materiales del protocolo, está protegido por derechos de autor y es propiedad exclusiva del creador.

6. Política de Reembolso
Debido a la naturaleza digital del producto y el acceso inmediato tras la compra, no se ofrecen reembolsos una vez entregado el acceso.

7. Limitación de Responsabilidad
No nos hacemos responsables de daños directos o indirectos derivados del uso del protocolo. El usuario asume toda la responsabilidad de seguir las recomendaciones bajo su propio criterio.

8. Ley Aplicable
Estos términos se rigen por las leyes aplicables en Estados Unidos.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-forest py-8 px-4" data-id="element-58">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4" data-id="element-59">
          <div className="flex items-center gap-6" data-id="element-60">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/70 hover:text-gold transition-colors text-sm underline underline-offset-2" data-id="element-61">
              Política de Privacidad
            </button>
            <span className="text-cream/30" data-id="element-62">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream/70 hover:text-gold transition-colors text-sm underline underline-offset-2" data-id="element-63">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-cream/40 text-xs" data-id="element-64">
            © {new Date().getFullYear()} Protocolo Anti-Inflamación 72h. Todos
            los derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-65">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-66">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-67">
              <div className="flex items-center justify-between p-6 border-b border-sage" data-id="element-68">
                <h3 className="font-serif text-xl font-bold text-forest" data-id="element-69">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-sage transition-colors" data-id="element-70">
                  <XIcon className="w-5 h-5 text-forest" data-id="element-71" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-72">
                <pre className="whitespace-pre-wrap font-sans text-gray-700 text-sm leading-relaxed" data-id="element-73">
                  {activeModal === 'privacy' ? privacyContent : termsContent}
                </pre>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}